import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import { home } from "../content/Home";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import heroImage from "../assets/images/8T6A0614.jpg";

const slideImages = [heroImage];

const Hero = () => {
  const paginationRef = useRef(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const heroContent = home.page[0];
  const mobileImagePosition = heroContent?.heroImagePositionMobile || "50% 30%";

  useEffect(() => {
    const timer = setTimeout(() => setOverlayVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isMobile]);

  const showHighlights = !isMobile || isExpanded;

  return (
    <section className="relative w-full overflow-hidden min-h-[100svh] pt-32 sm:pt-36 md:pt-0">
      <div className="absolute inset-0">
        <Swiper
          modules={[Pagination, Autoplay, EffectFade]}
          effect="fade"
          loop={slideImages.length > 1}
          autoplay={
            slideImages.length > 1
              ? { delay: 5000, disableOnInteraction: false }
              : false
          }
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.pagination.el = paginationRef.current;
          }}
          className="w-full h-full min-h-[100svh]"
        >
          {slideImages.map((image, index) => (
            <SwiperSlide key={index} className="!h-full">
              <div className="relative h-full min-h-[100svh] w-full">
                <img
                  src={image}
                  alt="BSS Sonata hero slide"
                  style={{ "--hero-mobile-pos": mobileImagePosition }}
                  className="absolute inset-0 h-full w-full object-cover object-[var(--hero-mobile-pos)] md:object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-transparent" />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="relative z-10 w-full">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-24">
          <div
            className={`relative max-w-xl sm:max-w-2xl mx-auto md:mx-0 transition-all duration-700 ease-out -mt-6 sm:mt-12 md:mt-20 ${
              overlayVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <div className="absolute inset-0 rounded-3xl border border-white/15 bg-black/45 backdrop-blur-2xl shadow-[0_35px_120px_rgba(0,0,0,0.65)]" />
            <div className="relative space-y-5 text-white px-5 py-7 rounded-3xl sm:px-8 sm:py-10 lg:px-10 lg:py-12">
              <div className="space-y-5">
                <h1
                  className="text-xl sm:text-4xl xl:text-5xl font-semibold leading-tight"
                  style={{ textShadow: "0 18px 36px rgba(0,0,0,0.6)" }}
                >
                  {heroContent.title}
                </h1>
                <p
                  className="text-[13px] sm:text-lg text-white/90 leading-relaxed max-w-xl text-justify"
                  style={{ textShadow: "0 8px 24px rgba(0,0,0,0.45)" }}
                >
                  {heroContent.description}
                </p>
                {showHighlights && (
                  <ul className="space-y-3 sm:space-y-2">
                    {heroContent.highlights.map((item) => (
                      <li key={item.title} className="flex items-start gap-3">
                        <span className="mt-1 inline-block h-8 w-[3px] rounded-full bg-red" />
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/70 sm:text-sm sm:tracking-[0.35em]">
                            {item.title}
                          </p>
                          <p className="text-[13px] text-white/90 sm:text-base text-justify">
                            {item.description}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="sm:hidden inline-flex items-center gap-2 text-white/80 text-[11px] font-semibold uppercase tracking-[0.35em]"
                >
                  {isExpanded ? "Read Less" : "Read More"}
                  <span className="text-base">{isExpanded ? "↑" : "↓"}</span>
                </button>
              )}
              <div className="pt-4">
                <Link
                  to={heroContent.cta.link || "/about"}
                  className="inline-flex w-full justify-center sm:w-auto sm:justify-start items-center gap-2 rounded-full bg-red px-5 py-2.5 text-sm sm:text-base font-semibold text-white shadow-[0_12px_30px_rgba(30,64,175,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-black"
                >
                  {heroContent.cta.text || "Learn more about us"}
                  <span aria-hidden="true" className="text-lg">
                    {heroContent.cta.icon || "→"}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20">
        <div
          ref={paginationRef}
          className="custom-pagination flex items-center gap-2"
        />
      </div>
    </section>
  );
};

export default Hero;
